import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HeroSection from './components/sections/HeroSection';
import SearchForm from './components/sections/SearchForm';
import FeaturedCarsSection from './components/sections/FeaturedCarsSection';
import WelcomeSection from './components/sections/WelcomeSection';
import TestimonialsSection from './components/sections/TestimonialsSection';
import WhyChooseUsSection from './components/sections/WhyChooseUsSection';
import CTASection from './components/sections/CTASection';
import VehicleDetailsPage from './components/pages/VehicleDetails';
import InventoryPage from './components/pages/Inventory';
import SearchResultsPage from './components/pages/SearchResults';
import WhatsAppButton from './components/ui/WhatsAppButton';
import { buildSearchUrl, vehiclePath } from './utils/urlHelpers';

// ---------------------------------------------------------------------------
// Scroll to top on every navigation
// ---------------------------------------------------------------------------
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ---------------------------------------------------------------------------
// Home page — handles deferred anchor scrolling when arriving from another route
// ---------------------------------------------------------------------------
function HomePage({ onSearch, onViewDetails, onSeeAll }) {
  const location = useLocation();

  useEffect(() => {
    document.title = 'JAC Cars | Premium Used Cars in Essex';
  }, []);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      const el = document.querySelector(target);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.state]);

  return (
    <>
      <HeroSection />
      <SearchForm onSearch={onSearch} />
      <FeaturedCarsSection onViewDetails={onViewDetails} onSeeAll={onSeeAll} />
      <WelcomeSection />
      <TestimonialsSection />
      <WhyChooseUsSection />
      <CTASection />
    </>
  );
}

// ---------------------------------------------------------------------------
// Root app — owns navigation handlers and route tree
// ---------------------------------------------------------------------------
function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (href) => {
    if (href === '#inventory') {
      navigate('/inventory');
      return;
    }
    // For all other anchors navigate home and pass the scroll target in state
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: href } });
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleViewDetails = (vehicle) => {
    navigate(vehiclePath(vehicle));
  };

  const handleSearch = (filters) => {
    navigate(buildSearchUrl(filters));
  };

  return (
    <div className="App">
      <ScrollToTop />
      <Header onLogoClick={() => navigate('/')} onNavigate={handleNavigate} />
      <main>
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                onSearch={handleSearch}
                onViewDetails={handleViewDetails}
                onSeeAll={() => navigate('/inventory')}
              />
            }
          />

          <Route
            path="/inventory"
            element={<InventoryPage onViewDetails={handleViewDetails} />}
          />

          {/* /stock/:make  → make-only search, e.g. /stock/audi */}
          <Route
            path="/stock/:make"
            element={<SearchResultsPage onViewDetails={handleViewDetails} />}
          />

          {/* /search?make=X&bodyType=Y&... → multi-filter search */}
          <Route
            path="/search"
            element={<SearchResultsPage onViewDetails={handleViewDetails} />}
          />

          {/* /cars/:stockId/:slug → vehicle detail */}
          <Route
            path="/cars/:stockId/:slug"
            element={<VehicleDetailsPage onViewDetails={handleViewDetails} />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
